import {
  DefaultCheckoutProbabilityBucket,
  getDefaultCheckoutProbabilityBucket,
} from "@redotech/redo-conversion-core-common/checkouts/segments";
import { ShopifyCart } from "@redotech/shopify-client/ajax";
import { isDef } from "@redotech/util/checks";
import { log } from "../../monitoring/log";

export const SHOW_DELAY_MS = 3000;
const MS_PER_SECOND = 1000;

export const STALE_PAGE_TIME_JITTER_SECONDS = 30;
export const STALE_PAGE_TIME_SECONDS = 90 - STALE_PAGE_TIME_JITTER_SECONDS / 2;

export interface ShouldShowPanelParams {
  hasDismissed: boolean;
  cumulativeConversionProbability: number | null;
  previousCumulativeConversionProbability: number | null;
  instantaneousConversionProbability: number | null;
  previousInstantaneousConversionProbability: number | null;
  mountTime: Temporal.Instant;
  cart: ShopifyCart | undefined;
  previewOn: boolean;
  dropoffPercentageTrigger: number;
  secondsInColdTrigger: number | undefined;
  /** When true, the panel may show even with an empty cart. */
  allowEmptyCart: boolean;
  /**
   * Inclusive minimum cart quantity for the stale-page fallback trigger. The
   * cart's total item quantity must be `>=` this value for that trigger to fire.
   */
  stalePageMinCartItems: number;
  sessionAgeInMs: number | null;
}

export const shouldShowPanel = (params: ShouldShowPanelParams): boolean => {
  if (params.previewOn) {
    console.warn("Showing rescue panel in preview mode");
    return true;
  }

  if (params.hasDismissed) {
    log("Not showing rescue panel, dismissed: ", params);
    return false;
  }

  // Empty carts suppress the panel unless the team opts in (e.g. buy-now
  // storefronts whose cart stays empty until checkout).
  if (!params.allowEmptyCart && !params.cart?.items.length) {
    log("Not showing rescue panel, empty cart: ", params);
    return false;
  }

  const now = Temporal.Now.instant();

  // Deterministic stale-page rule: if the cart's total quantity meets the
  // configured minimum and the page has been mounted for more than ~90 seconds
  // (with 15 second jitter), show the panel. This rule does not depend on the
  // hazard model and is the "instant-on" fallback for merchants whose model has
  // not been trained yet.
  const staleTime =
    STALE_PAGE_TIME_SECONDS +
    Math.floor(Math.random() * STALE_PAGE_TIME_JITTER_SECONDS);
  const totalCartQuantity =
    params.cart?.items.reduce((acc, item) => acc + item.quantity, 0) ?? 0;
  if (
    totalCartQuantity >= params.stalePageMinCartItems &&
    now.since(params.mountTime).total({ unit: "seconds" }) > staleTime
  ) {
    return true;
  }

  // Remaining triggers all require probability data from a trained model.
  if (
    !isDef(params.cumulativeConversionProbability) ||
    !isDef(params.instantaneousConversionProbability) ||
    !isDef(params.previousInstantaneousConversionProbability)
  ) {
    log("Not showing rescue panel, no probability data: ", params);
    return false;
  }

  const bucket = getDefaultCheckoutProbabilityBucket(
    Math.round(params.cumulativeConversionProbability * 100),
  );

  // Cold bucket trigger: if in COLD bucket for specified seconds, show panel
  // Since cumulative probability is non-decreasing, sessionAgeInMs equals time in cold
  if (
    bucket === DefaultCheckoutProbabilityBucket.COLD &&
    isDef(params.secondsInColdTrigger) &&
    isDef(params.sessionAgeInMs) &&
    params.sessionAgeInMs / MS_PER_SECOND >= params.secondsInColdTrigger
  ) {
    log("Showing rescue panel: cold bucket time trigger met");
    return true;
  }

  // Dropoff trigger only applies to cool and warm buckets
  if (
    bucket !== DefaultCheckoutProbabilityBucket.COOL &&
    bucket !== DefaultCheckoutProbabilityBucket.WARM
  ) {
    log("Should not show rescue panel, not in cool or warm bucket: ", bucket);
    return false;
  }

  // If within SHOW_DELAY_MS of mount, don't show
  if (
    now.since(params.mountTime).total({ unit: "milliseconds" }) < SHOW_DELAY_MS
  ) {
    log("Should not show rescue panel, within 3 seconds of mount time");
    return false;
  }

  // Show if instantaneous probability has dropped significantly
  const currentInstantaneousProb = params.instantaneousConversionProbability;
  const previousInstantaneousProb =
    params.previousInstantaneousConversionProbability;

  const shouldShow =
    currentInstantaneousProb <
    previousInstantaneousProb * (1 - params.dropoffPercentageTrigger / 100);
  log("Should show rescue panel result: ", shouldShow);
  return shouldShow;
};
